import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiLogOut } from "react-icons/fi";
import {
  MdClose,
  MdEdit,
  MdEmail,
  MdPhone,
  MdSave,
  MdVerified,
} from "react-icons/md";
import { TbSignature, TbUser, TbWallet } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { useAuth } from "../../Components/Context/AuthContext";
import RechargeModal from "../../Components/UserSideComponents/Recharge/RechargeModal";
import axios from "../../Utils/axiosInstance";
import { s3Upload } from "./../../Utils/s3Upload";
import EmailVerificationModal from "../../Components/UserSideComponents/Account/EmailVerificationModal";
import TransactionDetailModal from "../../Components/UserSideComponents/Account/TransactionDetailModal";
import { useLoadScript } from "@react-google-maps/api";
import { TbMapPin } from "react-icons/tb";
import AddressSelectModal from "../../Components/UserSideComponents/Home/Components/AddressSelectModal";
import AddAddressModalAPI from "../../Components/UserSideComponents/Home/Components/AddAddressModalAPI";

// ── Reusable labeled input wrapper for edit mode (dark/gradient bg) ───────────
const EditField = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wide">
      {label}
    </span>
    {children}
  </div>
);

const inputClass =
  "w-full px-3 py-1.5 rounded-lg bg-white/20 text-white placeholder:text-white/60 text-sm focus:outline-none focus:ring-2 focus:ring-white/50";

const BUSINESS_TYPE_OPTIONS = ["Pvt Ltd", "FPO / FPC", "Cooperative Society", "Other"];

const BusinessTypeSelect = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(
    value !== "" && !BUSINESS_TYPE_OPTIONS.slice(0, -1).includes(value)
  );

  const selectValue = showCustom ? "Other" : value;

  const handleSelectChange = (e) => {
    if (e.target.value === "Other") {
      setShowCustom(true);
      onChange("");
    } else {
      setShowCustom(false);
      onChange(e.target.value);
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <select
          value={selectValue}
          onChange={handleSelectChange}
          className="w-full px-3 py-1.5 rounded-lg bg-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50 appearance-none"
        >
          <option value="" disabled className="text-gray-800">
            Select Business Type
          </option>
          {BUSINESS_TYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt} className="text-gray-800">
              {opt}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          <svg className="w-3.5 h-3.5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {showCustom && (
        <input
          type="text"
          placeholder="Enter your business type"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          className="w-full px-3 py-1.5 rounded-lg bg-white/20 text-white placeholder:text-white/60 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
        />
      )}
    </div>
  );
};

// ── Transaction badge helpers ─────────────────────────────────────────────────
const txTypeBadge = (type) => {
  if (type === "credit" || type === "refund")
    return "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400";
  if (type === "debit")
    return "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400";
  return "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400";
};

const txStatusBadge = (status) => {
  if (status === "completed") return "text-green-600 dark:text-green-400";
  if (status === "failed" || status === "cancelled")
    return "text-red-500 dark:text-red-400";
  if (status === "pending" || status === "processing")
    return "text-yellow-500 dark:text-yellow-400";
  return "text-gray-500 dark:text-gray-400";
};

// ─────────────────────────────────────────────────────────────────────────────

const Account = () => {
  const [data, setData] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    companyName: "",
    designation: "",
    IEC: "",
    e_signature: "",
    businessType: "",
  });
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("draw");
  const [signatureType, setSignatureType] = useState(null);
  const hasExistingSignature = !!data?.user?.e_signature;
  const sigRef = useRef();

  // ── Profile photo state ──────────────────────────────────────────────────
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const profilePicInputRef = useRef();

  // ── Transaction history state ────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPagination, setTxPagination] = useState(null);
  const [txPage, setTxPage] = useState(1);
  const [txFilter, setTxFilter] = useState({ type: "", status: "" });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [txDetailModalOpen, setTxDetailModalOpen] = useState(false);

  // ── Recharge modal state ─────────────────────────────────────────────────
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);

  // Add to your state variables
  const [emailChangeMode, setEmailChangeMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailVerificationModalOpen, setEmailVerificationModalOpen] =
    useState(false);

  // ── Address state ────────────────────────────────────────────────────────
  const [addresses, setAddresses] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [addressType, setAddressType] = useState("Office");
  const [editAddress, setEditAddress] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [deletingAddressId, setDeletingAddressId] = useState(null);

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Add these functions
  const handleChangeEmail = () => {
    if (!newEmail) {
      return toast.error("Enter new email address");
    }
    if (!newEmail.includes("@")) {
      return toast.error("Enter valid email address");
    }
    setEmailVerificationModalOpen(true);
  };

  // Update the handleEmailVerified function
  const handleEmailVerified = async (verifiedEmail, isVerified = true) => {
    try {
      // Update the email in formData
      setFormData((prev) => ({
        ...prev,
        email: verifiedEmail,
      }));

      // Update user data to reflect verified status
      setData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          email: verifiedEmail,
          isEmailVerified: isVerified, // ✅ Mark as verified
        },
      }));

      setNewEmail("");
      setEmailChangeMode(false);
      toast.success("Email verified successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update email");
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/get-profile`,
      );
      setData(res.data.data);
      const user = res.data.data.user;
      if (user.e_signature) {
        setSignaturePreview(user.e_signature);
        setSignatureUrl(user.e_signature);
      }
      if (user.profilePic) {
        setProfilePicPreview(user.profilePic);
        setProfilePicUrl(user.profilePic);
      }
      setFormData({
        firstName: user.firstName || "",
        middleName: user.middleName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        countryCode: user.countryCode || "",
        companyName: user.companyName || "",
        designation: user.designation || "",
        IEC: user.IEC || "",
        e_signature: user.e_signature || "",
        businessType: user.businessType || "",
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/address`,
      );
      const addressData = res?.data?.data || [];
      setAddresses(addressData);
      const def = addressData.find((a) => a.isDefault);
      setDefaultAddress(def);
    } catch (err) {
      console.error("Failed to fetch addresses:", err);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    // if (!window.confirm("Are you sure you want to delete this address?")) return;

    try {
      setDeletingAddressId(addressId);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/address/${addressId}`,
      );
      toast.success("Address deleted successfully");
      await fetchAddresses();
    } catch (err) {
      console.error("Failed to delete address:", err);
      toast.error(err.response?.data?.message || "Failed to delete address");
    } finally {
      setDeletingAddressId(null);
    }
  };

  const fetchTransactions = async (userId, page = 1, filters = {}) => {
    setTxLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.type) params.append("type", filters.type);
      if (filters.status) params.append("status", filters.status);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/get-wallet-transactions/${userId}?${params.toString()}`,
      );
      if (res.data.success) {
        setTransactions(res.data.data.transactions);
        setTxPagination(res.data.data.pagination);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      setTxLoading(false);
    }
  };

  // ── After successful recharge: refresh both wallet & transactions ─────────
  const handleRechargeSuccess = () => {
    fetchProfile();
    if (data?.user?.id) {
      setTxPage(1);
      fetchTransactions(data.user.id, 1, txFilter);
    }
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setTxDetailModalOpen(true);
  };

  const handleCloseTxDetailModal = () => {
    setSelectedTransaction(null);
    setTxDetailModalOpen(false);
  };

  useEffect(() => {
    fetchProfile();
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (data?.user?.id) {
      fetchTransactions(data.user.id, txPage, txFilter);
    }
  }, [data?.user?.id, txPage, txFilter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Profile photo upload ─────────────────────────────────────────────────
  const handleProfilePicUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);

    // ✅ only preview
    setProfilePicPreview(localPreview);

    // ✅ store file for later upload
    setProfilePicUrl(file);
  };

  // ── Save drawn signature ─────────────────────────────────────────────────
  const handleSaveDraw = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      return toast.error("Please draw your signature");
    }
    const dataUrl = sigRef.current.toDataURL("image/png");
    setSignaturePreview(dataUrl);
    setSignatureType("draw");
    setSignatureUrl(dataUrl);
    toast.success("Signature saved");
  };

  // ── Upload signature image ───────────────────────────────────────────────
  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localPreview = URL.createObjectURL(file);
    setSignaturePreview(localPreview);
    setSignatureType("upload");
    try {
      const uploadToast = toast.loading("Uploading signature...");
      const res = await s3Upload(file, "signatures");
      setSignatureUrl(res.url);
      toast.dismiss(uploadToast);
      toast.success("Signature uploaded");
    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatePayload = {
        firstName: formData.firstName,
        middleName: formData.middleName || undefined,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        countryCode: formData.countryCode,
        companyName: formData.companyName || undefined,
        designation: formData.designation || undefined,
        IEC: formData.IEC ? formData.IEC.toUpperCase() : undefined,
        businessType: formData.businessType || undefined,
      };

      // ✅ OPTIMIZATION: Only send e_signature if it was actually changed
      // Check if the signature URL is different from the original
      if (signatureUrl && signatureUrl !== data?.user?.e_signature) {
        updatePayload.e_signature = signatureUrl;
      }

      let uploadedProfilePic = profilePicUrl;

      // Only upload photo if it's a new file (not a URL string)
      if (profilePicUrl instanceof File) {
        try {
          const uploadToast = toast.loading("Uploading photo...");
          const res = await s3Upload(profilePicUrl, "profiles");
          uploadedProfilePic = res.url;
          toast.dismiss(uploadToast);
        } catch (err) {
          toast.error("Photo upload failed");
          setSaving(false);
          return;
        }
      }

      // Only send profilePic if it changed
      if (uploadedProfilePic !== data?.user?.profilePic) {
        updatePayload.profilePic = uploadedProfilePic;
      }

      // Add a request timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

      try {
        const res = await axios.patch(
          `${process.env.REACT_APP_API_URL}/user/update/${data.user.id}`,
          updatePayload,
          { signal: controller.signal }
        );

        clearTimeout(timeoutId);

        if (res.data.success) {
          await fetchProfile();
          setIsEditing(false);
          toast.success("Profile updated successfully!");
        }
      } catch (err) {
        clearTimeout(timeoutId);

        if (err.code === 'ECONNABORTED') {
          toast.error("Request timeout. Please check your connection and try again.");
        } else {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to update profile");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const user = data.user;
    setFormData({
      firstName: user.firstName || "",
      middleName: user.middleName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      countryCode: user.countryCode || "",
      companyName: user.companyName || "",
      designation: user.designation || "",
      IEC: user.IEC || "",
      e_signature: user.e_signature || "",
      businessType: user.businessType || "",
    });
    setSignatureUrl(user.e_signature || null);
    setSignaturePreview(user.e_signature || null);
    setSignatureType(null);
    setProfilePicUrl(user.profilePic || null);
    setProfilePicPreview(user.profilePic || null);
    setIsEditing(false);
    if (profilePicInputRef.current) {
      profilePicInputRef.current.value = "";
    }
    setActiveTab("draw");
    if (sigRef.current) sigRef.current.clear();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Skeleton
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-4 lg:p-6 2xl:max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 p-3 md:gap-5">
          <div className="lg:col-span-1 space-y-5">
            <div className="relative overflow-hidden rounded-2xl p-3 md:p-5 bg-gradient-to-br from-primaryColor/20 to-subPrimaryColor/20 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/30" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 bg-white/30 rounded" />
                  <div className="h-3 w-24 bg-white/20 rounded" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 w-52 bg-white/20 rounded" />
                <div className="h-3 w-40 bg-white/20 rounded" />
              </div>
            </div>
            <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5 animate-pulse">
              <div className="h-4 w-32 bg-skeletonLoaderColor rounded mb-4" />
              <div className="h-32 rounded-xl bg-skeletonLoaderColor" />
            </div>
          </div>
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5 animate-pulse">
              <div className="h-4 w-32 bg-skeletonLoaderColor rounded mb-4" />
              <div className="h-20 rounded-xl bg-skeletonLoaderColor mb-4" />
              <div className="grid grid-cols-2 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-xl bg-skeletonLoaderColor h-14"
                  />
                ))}
              </div>
              <div className="flex justify-between items-center mt-4">
                <div className="h-3 w-20 bg-skeletonLoaderColor rounded" />
                <div className="h-6 w-16 bg-skeletonLoaderColor rounded-full" />
              </div>
            </div>
            <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5 animate-pulse">
              <div className="h-4 w-32 bg-skeletonLoaderColor rounded mb-4" />
              <div className="space-y-3">
                <div className="flex justify-between">
                  <div className="h-3 w-24 bg-skeletonLoaderColor rounded" />
                  <div className="h-3 w-40 bg-skeletonLoaderColor rounded" />
                </div>
                <div className="flex justify-between">
                  <div className="h-3 w-24 bg-skeletonLoaderColor rounded" />
                  <div className="h-3 w-28 bg-skeletonLoaderColor rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const user = data?.user;
  const wallet = data?.wallet;

  return (
    <div className="p-2 md:p-4 pb-24 md:pb-0 lg:p-6 2xl:max-w-7xl mx-auto">
      {/* Edit Button - Fixed Position */}
      <div className="fixed bottom-[4.5rem] md:bottom-6 right-3 md:right-6 z-50 flex flex-col gap-3">
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-5 py-3 bg-primaryColor text-white rounded-full shadow-lg hover:scale-105 transition"
          >
            <MdEdit size={18} />
            Edit
          </button>
        ) : (
          <>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-3 bg-green-600 text-white rounded-full shadow-lg hover:scale-105 transition disabled:opacity-50"
            >
              <MdSave size={18} />
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-3 bg-gray-700 text-white rounded-full shadow-lg hover:scale-105 transition"
            >
              <MdClose size={18} />
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 p-3 md:gap-5">
        {/* ═══════════════ LEFT COLUMN ═══════════════ */}
        <div className="lg:col-span-1 space-y-5">
          {/* ───────── Profile Card ───────── */}
          <div className="relative overflow-hidden rounded-2xl p-3 md p-3:md:p-5 bg-gradient-to-br from-primaryColor to-subPrimaryColor text-white shadow-lg">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />

            <div className="relative">
              <div className="flex items-center gap-4 mb-4">
                {/* Avatar with photo upload */}
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center text-xl font-bold overflow-hidden">
                    {profilePicPreview ? (
                      <img
                        src={profilePicPreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      user?.firstName?.[0]
                    )}
                  </div>
                  {isEditing && (
                    <>
                      <button
                        type="button"
                        onClick={() => profilePicInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-md"
                        title="Upload photo"
                      >
                        {uploadingPhoto ? (
                          <svg
                            className="animate-spin w-3 h-3 text-primaryColor"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                        ) : (
                          <MdEdit size={11} className="text-primaryColor" />
                        )}
                      </button>
                      <input
                        ref={profilePicInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfilePicUpload}
                      />
                    </>
                  )}
                </div>

                {/* View mode: name block next to avatar */}
                {!isEditing && (
                  <div className="flex-1">
                    <h2 className="text-lg font-bold">
                      {user?.fullName || `${user?.firstName} ${user?.lastName}`} <span className="text-xs font-normal text-white/70">({user?.role})</span>
                    </h2>
                    {user?.companyName && (
                      <p className="text-xs text-white/70 mt-0.5">
                        {user.companyName}
                      </p>
                    )}
                    {user?.designation && (
                      <p className="text-xs text-white/60">
                        {user.designation}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Edit mode: all labeled fields */}
              {isEditing && (
                <div className="space-y-3 mb-4">
                  {/* Legal Representative group */}
                  <div className="space-y-2">
                    {/* <p className="text-[9px] font-semibold text-white/50 uppercase tracking-widest">
                      Legal Representative
                    </p> */}
                    <EditField label="First Name">
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="First Name"
                        className={inputClass}
                      />
                    </EditField>
                    <EditField label="Middle Name (optional)">
                      <input
                        type="text"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleInputChange}
                        placeholder="Middle Name"
                        className={inputClass}
                      />
                    </EditField>
                    <EditField label="Last Name">
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Last Name"
                        className={inputClass}
                      />
                    </EditField>
                  </div>

                  <div className="border-t border-white/20" />

                  <EditField label="Company Name">
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Company Name"
                      className={inputClass}
                    />
                  </EditField>

                  <EditField label="Business Type">
                    <BusinessTypeSelect
                      value={formData.businessType}
                      onChange={(val) => setFormData((prev) => ({ ...prev, businessType: val }))}
                    />
                  </EditField>

                  {/* ── IEC moved here: directly below Company Name in edit mode ── */}
                  <EditField label="IEC Number (optional)">
                    <input
                      type="text"
                      name="IEC"
                      maxLength={10}
                      value={formData.IEC}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          IEC: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, ""),
                        }))
                      }
                      placeholder="10-digit alphanumeric"
                      className={inputClass}
                    />
                  </EditField>

                  <EditField label="Designation">
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="Designation"
                      className={inputClass}
                    />
                  </EditField>
                </div>
              )}

              {/* Email & Phone rows */}
              <div className="space-y-3 text-sm w-full">
                <div className="flex items-center gap-2 text-white/90">
                  <MdEmail size={16} className="flex-shrink-0" />
                  {!isEditing ? (
                    <>
                      <span className="flex-1">{user?.email}</span>
                      {user?.isEmailVerified ? (
                        <span className="flex items-center gap-1 text-green-300 text-xs">
                          <MdVerified size={14} /> Verified
                        </span>
                      ) : (
                        // ✅ Show Verify button if email is NOT verified
                        <button
                          onClick={() => {
                            setNewEmail(user?.email);
                            setEmailVerificationModalOpen(true);
                          }}
                          className="text-xs px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white transition font-medium"
                        >
                          Verify Now
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 w-full">
                      {!emailChangeMode ? (
                        // Show: Email field + Change button
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <EditField label="Email">
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Email"
                                className={inputClass}
                                disabled
                              />
                            </EditField>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEmailChangeMode(true)}
                            className="px-3 py-2 rounded-lg bg-primaryColor/50 hover:bg-primaryColor text-white text-xs font-medium transition h-fit"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        // Show: New email input + Verify Email button
                        <div className="w-full space-y-3">
                          <EditField label="New Email Address">
                            <input
                              type="email"
                              value={newEmail}
                              onChange={(e) => setNewEmail(e.target.value)}
                              placeholder="Enter your new email"
                              className={inputClass}
                            />
                          </EditField>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleChangeEmail}
                              className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition"
                            >
                              Verify Email
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEmailChangeMode(false);
                                setNewEmail("");
                              }}
                              className="flex-1 py-2.5 rounded-lg bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Phone section */}
                <div className="flex items-center gap-2 text-white/90">
                  <MdPhone size={16} className="flex-shrink-0" />
                  {!isEditing ? (
                    <>
                      <span className="flex-1">{user?.phone}</span>
                      {user?.isPhoneVerified && (
                        <span className="flex items-center gap-1 text-green-300 text-xs">
                          <MdVerified size={14} /> Verified
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 flex gap-2">
                      <div className="w-16">
                        <EditField label="Code">
                          <input
                            type="text"
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleInputChange}
                            placeholder="+91"
                            className={inputClass}
                          />
                        </EditField>
                      </div>
                      <div className="flex-1">
                        <EditField label="Phone Number">
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Phone Number"
                            className={inputClass}
                          />
                        </EditField>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ───────── E-Signature Card ───────── */}
          <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TbSignature className="text-primaryColor" size={20} />
              <h3 className="font-bold text-primaryTextColor dark:text-white">
                E-Signature
              </h3>
            </div>

            {!isEditing || data?.user?.e_signature ? (
              <div>
                {signaturePreview ? (
                  <div className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-xl p-4 bg-gray-50 dark:bg-white/5">
                    <img
                      src={signaturePreview}
                      alt="E-Signature"
                      className="max-h-32 mx-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Existing signature already present */}
                    {hasExistingSignature ? (
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-xl p-4 bg-gray-50 dark:bg-white/5">
                          <img
                            src={signaturePreview}
                            alt="E-Signature"
                            className="max-h-32 mx-auto object-contain"
                          />
                        </div>

                        <div className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
                          <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
                            Signature already submitted and cannot be edited.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Tabs */}
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setActiveTab("draw");
                              setSignatureType(null);
                            }}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition ${activeTab === "draw"
                              ? "bg-primaryColor text-white shadow"
                              : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400"
                              }`}
                          >
                            Draw
                          </button>

                          <button
                            onClick={() => {
                              setActiveTab("upload");
                              setSignatureType(null);
                            }}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition ${activeTab === "upload"
                              ? "bg-primaryColor text-white shadow"
                              : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400"
                              }`}
                          >
                            Upload
                          </button>
                        </div>

                        {/* DRAW */}
                        {activeTab === "draw" && (
                          <div className="space-y-3">
                            <div className="border border-gray-300 dark:border-white/20 rounded-xl overflow-hidden bg-white">
                              <SignatureCanvas
                                ref={sigRef}
                                penColor="black"
                                canvasProps={{
                                  className: "w-full h-40",
                                }}
                              />
                            </div>

                            <div className="flex gap-3">
                              <button
                                type="button"
                                onClick={() => sigRef.current.clear()}
                                className="flex-1 py-2 rounded-xl border border-gray-300 dark:border-white/20 text-sm"
                              >
                                Clear
                              </button>

                              <button
                                type="button"
                                onClick={handleSaveDraw}
                                className="flex-1 py-2 rounded-xl bg-primaryColor text-white text-sm"
                              >
                                Save Signature
                              </button>
                            </div>
                          </div>
                        )}

                        {/* UPLOAD */}
                        {activeTab === "upload" && (
                          <div className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-xl p-6 text-center">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={handleSignatureUpload}
                              className="hidden"
                              id="signatureUpload"
                            />

                            <label
                              htmlFor="signatureUpload"
                              className="cursor-pointer flex flex-col items-center gap-2"
                            >
                              <span className="text-3xl">📤</span>

                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Click to upload or open camera
                              </span>

                              <span className="text-xs text-gray-400">
                                PNG, JPG supported
                              </span>
                            </label>
                          </div>
                        )}

                        {signaturePreview && (
                          <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/20 rounded-xl p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              Preview
                            </p>

                            <img
                              src={signaturePreview}
                              alt="signature"
                              className="h-20 border border-gray-200 dark:border-white/20 rounded mx-auto"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setActiveTab("draw");
                      setSignatureType(null);
                    }}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition ${activeTab === "draw" ? "bg-primaryColor text-white shadow" : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400"}`}
                  >
                    Draw
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("upload");
                      setSignatureType(null);
                    }}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition ${activeTab === "upload" ? "bg-primaryColor text-white shadow" : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400"}`}
                  >
                    Upload
                  </button>
                </div>

                {activeTab === "draw" && (
                  <div className="border-2 border-gray-200 dark:border-white/20 rounded-2xl bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                    <SignatureCanvas
                      ref={sigRef}
                      penColor="black"
                      canvasProps={{ className: "w-full h-40" }}
                    />
                    <div className="flex justify-between px-4 py-3 border-t border-gray-200 dark:border-white/20 bg-gray-50 dark:bg-white/5">
                      <button
                        onClick={() => sigRef.current.clear()}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleSaveDraw}
                        className="text-sm font-medium text-primaryColor hover:text-primaryColor/80"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "upload" && (
                  <div className="border-2 border-dashed border-gray-300 dark:border-white/20 rounded-2xl p-6 text-center bg-white dark:bg-white/5">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUpload}
                      className="hidden"
                      id="signatureUpload"
                    />
                    <label
                      htmlFor="signatureUpload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <span className="text-3xl">📤</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Click to upload or open camera
                      </span>
                      <span className="text-xs text-gray-400">
                        PNG, JPG supported
                      </span>
                    </label>
                  </div>
                )}

                {signaturePreview && (
                  <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/20 rounded-xl p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Preview
                    </p>
                    <img
                      src={signaturePreview}
                      alt="signature"
                      className="h-20 border border-gray-200 dark:border-white/20 rounded mx-auto"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ───────── Account Info ───────── */}
          <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5">
            <div className="flex items-center gap-2 mb-4">
              <TbUser className="text-primaryColor" size={20} />
              <h3 className="font-bold text-primaryTextColor dark:text-white">
                Account Info
              </h3>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-secondaryTextColor">Tag</span>
                <span className="font-semibold">{user?.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondaryTextColor">Joined</span>
                <span>
                  {new Date(user?.createdAt).toLocaleDateString("en-IN")}
                </span>
              </div>

              {/* Business sub-section */}
              <div className="border-t border-gray-100 dark:border-white/10 pt-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Business
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-secondaryTextColor">Company</span>
                    <span className="font-medium text-right">
                      {user?.companyName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondaryTextColor">Designation</span>
                    <span className="font-medium text-right">
                      {user?.designation || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondaryTextColor">Business Type</span>
                    <span className="font-medium text-right">{user?.businessType || "—"}</span>
                  </div>

                  {/* GST — always read-only */}
                  <div className="flex justify-between">
                    <span className="text-secondaryTextColor">GST</span>
                    <span className="font-mono text-xs uppercase">
                      {user?.GST || "—"}
                    </span>
                  </div>

                  {/* IEC — always read-only here (edit moved to profile card) */}
                  <div className="flex justify-between">
                    <span className="text-secondaryTextColor">IEC</span>
                    <span className="font-mono text-xs uppercase">
                      {user?.IEC || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Address Card ───────── */}
          <div className="bg-white dark:bg-[#ffffff1a] border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TbMapPin className="text-primaryColor" size={20} />
                <h3 className="font-bold text-primaryTextColor dark:text-white">
                  Delivery Addresses
                </h3>
              </div>
              <button
                onClick={() => {
                  setAddressType("Office");
                  setEditAddress(null);
                  setShowAddAddressModal(true);
                }}
                className="text-xs px-2.5 py-1.5 rounded-lg bg-primaryColor text-white hover:bg-primaryColor/90 transition font-medium"
              >
                + Add
              </button>
            </div>

            {loadingAddresses ? (
              <div className="space-y-2">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 rounded-xl bg-gray-100 dark:bg-white/5 animate-pulse"
                  />
                ))}
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-6 text-secondaryTextColor text-sm">
                <p className="mb-3">No addresses saved yet</p>
                <button
                  onClick={() => {
                    setAddressType("Office");
                    setEditAddress(null);
                    setShowAddAddressModal(true);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-primaryColor/10 text-primaryColor hover:bg-primaryColor hover:text-white transition font-medium"
                >
                  Add Your First Address
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {addresses.map((addr) => {
                  const isOffice = addr.type === "Office";
                  const typeIcon = isOffice ? "🏢" : "🏭";
                  const typeBg = isOffice
                    ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                    : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400";

                  return (
                    <div
                      key={addr._id}
                      onClick={() => setShowAddressModal(true)}
                      className="p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 hover:border-primaryColor/50 cursor-pointer transition-all hover:shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeBg}`}>
                              {typeIcon} {addr.type}
                            </span>
                            {addr.isDefault && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 font-semibold">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-secondaryTextColor dark:text-darkModeSecondaryTextColor truncate">
                            {addr.addressLine1}
                          </p>
                          {addr.addressLine2 && (
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                              {addr.addressLine2}
                            </p>
                          )}
                          <p className="text-[10px] text-secondaryTextColor dark:text-darkModeSecondaryTextColor mt-0.5">
                            {addr.city}, {addr.state} {addr.postalCode}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditAddress(addr);
                              setAddressType(addr.type);
                              setShowAddAddressModal(true);
                            }}
                            className="text-xs px-2 py-1.5 rounded-lg bg-white dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/20 transition font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAddress(addr._id);
                            }}
                            disabled={deletingAddressId === addr._id}
                            className="text-xs px-2 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 transition font-medium disabled:opacity-50"
                          >
                            {deletingAddressId === addr._id ? "..." : "Del"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════ RIGHT COLUMN ═══════════════ */}
        <div className="lg:col-span-2 space-y-5 mt-3 md:mt-0">
          {/* ───────── Wallet Card ───────── */}
          <div className="bg-white dark:bg-[#ffffff1a] backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TbWallet className="text-primaryColor" size={20} />
              <h3 className="font-bold text-primaryTextColor dark:text-white">
                Wallet Overview
              </h3>
            </div>

            {/* Balance — with Add Money button */}
            <div className="bg-gradient-to-r from-primaryColor to-subPrimaryColor text-white rounded-xl md:p-5 p-3 mb-5 shadow-lg">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-sm opacity-80 mb-1">Available Balance</p>
                  <h2 className="text-3xl font-bold">
                    {wallet?.currency === "USD" ? "$" : "₹"}
                    {wallet?.availableBalance}
                  </h2>
                </div>
                <button
                  onClick={() => setRechargeModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-semibold backdrop-blur-sm border border-white/30 transition hover:scale-105 active:scale-95"
                >
                  <span className="text-base leading-none font-bold">+</span>
                  Add Money
                </button>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-500/10 dark:to-purple-600/10 border border-purple-200 dark:border-purple-500/20">
                <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                  Currency
                </p>
                <p className="font-bold text-lg text-purple-700 dark:text-purple-300">
                  {wallet?.currency}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 dark:from-green-500/10 dark:to-green-600/10 border border-green-200 dark:border-green-500/20">
                <p className="text-xs text-green-600 dark:text-green-400 mb-1">
                  Total Recharged
                </p>
                <p className="font-bold text-lg text-green-700 dark:text-green-300">
                  {wallet?.currency === "USD" ? "$" : "₹"}
                  {wallet?.totalRecharged}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-500/10 dark:to-orange-600/10 border border-orange-200 dark:border-orange-500/20">
                <p className="text-xs text-orange-600 dark:text-orange-400 mb-1">
                  Total Spent
                </p>
                <p className="font-bold text-lg text-orange-700 dark:text-orange-300">
                  {wallet?.currency === "USD" ? "$" : "₹"}
                  {wallet?.totalSpent}
                </p>
              </div>
            </div>

            {/* Wallet status */}
            <div className="mt-5 flex justify-between items-center pt-4 border-t border-gray-200 dark:border-white/10">
              <p className="text-sm text-secondaryTextColor">Wallet Status</p>
              <span
                className={`text-xs px-4 py-1.5 rounded-full font-semibold ${wallet?.status === "active" ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400" : "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400"}`}
              >
                {wallet?.status?.toUpperCase()}
              </span>
            </div>

            {/* ── Transaction History ─────────────────────────────────────── */}
            <div className="mt-6 pt-5 border-t border-gray-200 dark:border-white/10">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h4 className="font-bold text-sm text-primaryTextColor dark:text-white">
                  Transaction History
                </h4>

                {/* Filters */}
                <div className="flex gap-2">
                  <select
                    value={txFilter.type}
                    onChange={(e) => {
                      setTxPage(1);
                      setTxFilter((f) => ({ ...f, type: e.target.value }));
                    }}
                    className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/20 bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    <option value="">All Types</option>
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                    <option value="refund">Refund</option>
                    <option value="reversal">Reversal</option>
                    <option value="adjustment">Adjustment</option>
                  </select>

                  <select
                    value={txFilter.status}
                    onChange={(e) => {
                      setTxPage(1);
                      setTxFilter((f) => ({ ...f, status: e.target.value }));
                    }}
                    className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/20 bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300 focus:outline-none"
                  >
                    <option value="">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="on_hold">On Hold</option>
                  </select>
                </div>
              </div>

              {/* Transaction list */}
              {txLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="h-14 rounded-xl bg-gray-100 dark:bg-white/5 animate-pulse"
                    />
                  ))}
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-10 text-secondaryTextColor text-sm">
                  No transactions found
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((tx) => (
                    <div
                      key={tx._id}
                      onClick={() => handleTransactionClick(tx)}
                      className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 hover:border-primaryColor/50 dark:hover:border-primaryColor/50 cursor-pointer transition-all hover:shadow-md"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase flex-shrink-0 ${txTypeBadge(tx.type)}`}
                        >
                          {tx.type}
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-primaryTextColor dark:text-white truncate">
                            {tx.description}
                          </p>
                          <p className="text-[10px] text-secondaryTextColor mt-0.5">
                            {tx.transactionId} ·{" "}
                            {new Date(tx.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0 ml-3">
                        <p
                          className={`text-sm font-bold ${tx.type === "credit" || tx.type === "refund" ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}
                        >
                          {tx.type === "credit" || tx.type === "refund"
                            ? "+"
                            : "-"}
                          {wallet?.currency === "USD" ? "$" : "₹"}
                          {tx.amount}
                        </p>
                        <p
                          className={`text-[10px] font-medium capitalize ${txStatusBadge(tx.status)}`}
                        >
                          {tx.status}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {txPagination && txPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-white/10">
                  <p className="text-xs text-secondaryTextColor">
                    Page {txPagination.currentPage} of {txPagination.totalPages}{" "}
                    · {txPagination.totalTransactions} total
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={txPage <= 1}
                      onClick={() => setTxPage((p) => p - 1)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/20 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    >
                      Prev
                    </button>
                    <button
                      disabled={!txPagination.hasMore}
                      onClick={() => setTxPage((p) => p + 1)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/20 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ───────── Logout Card ───────── */}
          <div className="bg-white dark:bg-[#ffffff1a] border border-red-200 dark:border-red-500/20 rounded-2xl p-3 md:p-4">
            <button
              onClick={() => logout()}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl
      bg-red-50 hover:bg-red-100
      dark:bg-red-500/10 dark:hover:bg-red-500/20
      text-red-600 dark:text-red-400
      font-semibold transition-all duration-200"
            >
              <FiLogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Recharge Modal ────────────────────────────────────────────────── */}
      <RechargeModal
        isOpen={rechargeModalOpen}
        onClose={() => setRechargeModalOpen(false)}
        wallet={wallet}
        user={user}
        onSuccess={handleRechargeSuccess}
      />

      {/* Email Verification Modal */}
      <EmailVerificationModal
        show={emailVerificationModalOpen}
        onClose={() => {
          setEmailVerificationModalOpen(false);
          setNewEmail("");
          setEmailChangeMode(false);
        }}
        onVerified={handleEmailVerified}
        newEmail={newEmail}
        isEmailChange={emailChangeMode}
      />

      <TransactionDetailModal
        isOpen={txDetailModalOpen}
        onClose={handleCloseTxDetailModal}
        transaction={selectedTransaction}
        wallet={wallet}
      />

      {/* ── Address Modals ────────────────────────────────────────────────── */}
      <AddressSelectModal
        showModal={showAddressModal}
        closeModal={() => setShowAddressModal(false)}
        addresses={addresses}
        setAddresses={setAddresses}
        setDefaultAddress={setDefaultAddress}
        refreshAddresses={fetchAddresses}
        openAddModal={(type, data) => {
          setAddressType(type);
          setEditAddress(data || null);
          setShowAddressModal(false);
          setTimeout(() => setShowAddAddressModal(true), 250);
        }}
      />

      {mapsLoaded && (
        <AddAddressModalAPI
          showModal={showAddAddressModal}
          closeModal={() => setShowAddAddressModal(false)}
          onBack={() => {
            setShowAddAddressModal(false);
            // setTimeout(() => setShowAddressModal(true), 250);
          }}
          refreshAddresses={fetchAddresses}
          type={addressType}
          editData={editAddress}
          addresses={addresses}
        />
      )}
    </div>
  );
};

export default Account;
